import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import applicationDetailSignal from '../ApplicationDetail.signal';
import {
  createLossClaim,
  updateLossClaim,
  deleteLossClaim,
  fetchApplicationDetails,
  fetchEdoUsers,
  syncApplicationDetailLoanDisplayFromDetailsPayload,
} from '../ApplicationDetail.helpers';
import alertSignal from 'signals/Alert.signal';
import $appSettings from 'signals/AppSettings.signal';
import { LossClaim } from 'types';
import currencyOptions from 'components/global/Constant/currencyOptions';
import CloseModalButton from 'components/global/Constant/CloseModalButton';

const DELIVERY_METHOD_OPTIONS = [
  { value: '', label: '—' },
  { value: 'E', label: 'E - Email' },
  { value: 'F', label: 'F - Fax' },
  { value: 'M', label: 'M - Mail' },
];

/** Frontend display names from ABS_CLA_CLAIM_PROCESSING (CALCAP FRED Tables w- Frontend names.csv) */
const CLAIM_FIELD_LABELS: Record<string, string> = {
  chargeOffDate: 'Loan Charge Off Date',
  claimSubmittedDate: 'Claim Submitted Date',
  amountPaidDate: 'Date Claim Amount Paid',
  filedAmount: 'Amount Filed',
  approvedClaimAmount: 'Claim Amount Approved',
  submitted: 'Claim Submitted',
  loanSold: 'Was the Loan Sold?',
  deliveryMethod: 'Delivery Method',
  receivedByUserId: 'App Received By',
  createdDate: 'Created Date',
};

function parseCurrency(value: string): number | null {
  if (!value || value.trim() === '') return null;
  const cleaned = value.replace(/[$,\s]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatDateForInput(
  val: string | Date | number | boolean | null | undefined
): string {
  if (val == null) return '';
  const d = new Date(val as string | number);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

const emptyForm = (): Record<string, string | number | boolean | null> => ({
  chargeOffDate: '',
  claimSubmittedDate: '',
  amountPaidDate: '',
  filedAmount: '',
  approvedClaimAmount: '',
  submitted: false,
  loanSold: false,
  deliveryMethod: '',
  receivedByUserId: '',
});

const claimToForm = (c: LossClaim): Record<string, string | number | boolean | null> => {
  const toDate = (v: string | null | undefined) => {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  };
  const toAmount = (v: number | null | undefined) => {
    if (v == null || Number.isNaN(Number(v))) return '';
    return Number(v);
  };
  return {
    chargeOffDate: toDate(c.chargeOffDate),
    claimSubmittedDate: toDate(c.claimSubmittedDate),
    amountPaidDate: toDate(c.amountPaidDate),
    filedAmount: toAmount(c.filedAmount),
    approvedClaimAmount: toAmount(c.approvedClaimAmount),
    submitted: c.submitted === true,
    loanSold:
      c.loanSold === 'Y' ||
      c.loanSold === '1' ||
      String(c.loanSold).toLowerCase() === 'true',
    deliveryMethod: c.deliveryMethod ?? '',
    receivedByUserId: c.receivedByUserId ?? '',
  };
};

const formToBody = (
  form: Record<string, string | number | boolean | null>
): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  const dateFields = [
    'chargeOffDate',
    'claimSubmittedDate',
    'amountPaidDate',
  ];
  dateFields.forEach((f) => {
    const v = form[f];
    if (v !== '' && v != null) body[f] = v;
  });
  const amountFields = [
    'filedAmount',
    'approvedClaimAmount',
  ];
  amountFields.forEach((f) => {
    const v = form[f];
    if (v !== '' && v != null) {
      const n = Number(v);
      if (!Number.isNaN(n)) body[f] = n;
    }
  });
  if (form.submitted != null) body.submitted = form.submitted;
  body.loanSold = form.loanSold === true ? 'Y' : 'N';
  if (form.deliveryMethod !== '') body.deliveryMethod = form.deliveryMethod;
  body.receivedByUserId =
    form.receivedByUserId !== '' && form.receivedByUserId != null
      ? String(form.receivedByUserId)
      : null;
  return body;
};

type EdoUser = { id: number | string; firstName?: string; lastName?: string; displayName?: string };

function getEdoUserDisplayName(user: EdoUser): string {
  if (user.displayName) return user.displayName;
  const first = user.firstName ?? '';
  const last = user.lastName ?? '';
  return [first, last].filter(Boolean).join(' ') || String(user.id);
}

const ApplicationClaimsTab: React.FC = () => {
  const {
    lossClaims,
    id: applicationId,
    calcapLoanNumber,
    lenderLoanNumber,
    lender,
    status: applicationStatus,
  } = applicationDetailSignal.value;
  const lenderCalcapIdDisplay =
    lender?.otherOrganizationId != null && String(lender.otherOrganizationId).trim() !== ''
      ? String(lender.otherOrganizationId).trim()
      : '—';
  const { APPLICATION_STATUS } = $appSettings.value.constants;
  const isDraftApplication = applicationStatus === APPLICATION_STATUS.inProgress;
  const claims: LossClaim[] = Array.isArray(lossClaims) ? lossClaims : [];
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [form, setForm] = useState<Record<string, string | number | boolean | null>>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [amountFocused, setAmountFocused] = useState<string | null>(null);
  const [amountRaw, setAmountRaw] = useState<Record<string, string>>({});
  const [edoUsers, setEdoUsers] = useState<EdoUser[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const users = await fetchEdoUsers();
      if (!cancelled) setEdoUsers(Array.isArray(users) ? users : []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load loss claims from details API (amounts in dollars).
  useEffect(() => {
    if (applicationId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const details = await fetchApplicationDetails(applicationId);
        if (!cancelled && details) {
          if (Array.isArray(details?.lossClaims)) {
            applicationDetailSignal.update({ lossClaims: details.lossClaims });
          }
          syncApplicationDetailLoanDisplayFromDetailsPayload(
            details as Record<string, unknown>
          );
        }
      } catch (e) {
        if (!cancelled) console.error('Failed to load claims', e);
      }
    })();
    return () => { cancelled = true; };
  }, [applicationId]);

  useEffect(() => {
    if (isDraftApplication && showModal && editingId === null) {
      setShowModal(false);
      setAmountRaw({});
      setAmountFocused(null);
    }
  }, [isDraftApplication, showModal, editingId]);

  const openCreate = useCallback(() => {
    if (isDraftApplication) return;
    setEditingId(null);
    setForm(emptyForm());
    setAmountRaw({});
    setAmountFocused(null);
    setShowModal(true);
  }, [isDraftApplication]);

  const openEdit = useCallback((c: LossClaim) => {
    setEditingId(c.id);
    setForm(claimToForm(c));
    setAmountRaw({});
    setAmountFocused(null);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingId(null);
    setAmountFocused(null);
    setAmountRaw({});
  }, []);

  const refreshClaims = useCallback(async () => {
    if (applicationId == null) return;
    try {
      const details = await fetchApplicationDetails(applicationId);
      applicationDetailSignal.update({
        lossClaims: Array.isArray(details?.lossClaims) ? details.lossClaims : [],
      });
    } catch (e) {
      console.error('Failed to refresh claims', e);
    }
  }, [applicationId]);

  const handleSubmit = useCallback(async () => {
    if (applicationId == null) return;
    setSaving(true);
    try {
      const body = formToBody(form);
      if (editingId != null) {
        await updateLossClaim(editingId, body);
        alertSignal.update({ type: 'notification', message: 'Claim updated successfully.', variant: 'success' });
      } else {
        await createLossClaim(applicationId, body);
        alertSignal.update({ type: 'notification', message: 'Claim created successfully.', variant: 'success' });
      }
      closeModal();
      await refreshClaims();
    } catch (err: any) {
      alertSignal.update({
        type: 'notification',
        message: err?.message || (editingId ? 'Failed to update claim.' : 'Failed to create claim.'),
        error: err,
      });
    } finally {
      setSaving(false);
    }
  }, [applicationId, editingId, form, closeModal, refreshClaims]);

  const handleDelete = useCallback(
    async (claim: LossClaim) => {
      if (applicationId == null || claim.id == null) return;
      if (!window.confirm('Are you sure you want to delete this claim?')) return;
      setDeletingId(claim.id);
      try {
        await deleteLossClaim(claim.id);
        alertSignal.update({ type: 'notification', message: 'Claim deleted successfully.', variant: 'success' });
        await refreshClaims();
      } catch (err: any) {
        alertSignal.update({
          type: 'notification',
          message: err?.message || 'Failed to delete claim.',
          variant: 'danger',
          error: err,
        });
      } finally {
        setDeletingId(null);
      }
    },
    [applicationId, refreshClaims]
  );

  const setField = useCallback(
    (field: string, value: string | number | boolean | null) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  /** Display value for a currency field: formatted when not focused, raw when focused. */
  const amountDisplayValue = (field: string): string => {
    const isFocused = amountFocused === field;
    const rawVal = amountRaw[field];
    if (isFocused && rawVal !== undefined && rawVal !== '') return rawVal;
    const v = form[field];
    const num =
      typeof v === 'number' ? v : v !== '' && v != null ? Number(v) : null;
    if (num != null && !Number.isNaN(num) && isFinite(num)) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    }
    return '';
  };

  const handleAmountFocus = useCallback((field: string) => {
    setAmountFocused(field);
    const v = form[field];
    if (v !== '' && v != null && !Number.isNaN(Number(v))) {
      setAmountRaw((prev) => ({
        ...prev,
        [field]: typeof v === 'number' ? v.toFixed(2) : String(v),
      }));
    } else {
      setAmountRaw((prev) => ({ ...prev, [field]: '' }));
    }
  }, [form]);

  const handleAmountBlur = useCallback((field: string) => {
    setAmountFocused(null);
    const rawValue = amountRaw[field] ?? '';
    const numValue = parseCurrency(rawValue);
    setForm((prev) => ({
      ...prev,
      [field]: numValue !== null && !Number.isNaN(numValue) ? numValue : '',
    }));
    setAmountRaw((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [amountRaw]);

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString(
      'en-US',
      currencyOptions.currencyUS as Intl.NumberFormatOptions
    );
  };

  /** API always returns filed/approved amounts in dollars. */
  const formatClaimAmount = (
    value: number | null | undefined,
    _field: 'filedAmount' | 'approvedClaimAmount'
  ) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return formatCurrency(Number(value));
  };

  /** Format as MM/DD/YYYY using the calendar date (no timezone shift). */
  const formatDate = (iso: string | null | undefined) => {
    if (!iso || typeof iso !== 'string') return '—';
    const dateOnly = iso.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return '—';
    const [y, m, d] = dateOnly.split('-');
    return `${m}/${d}/${y}`;
  };

  const sortedClaims = [...claims].sort((a, b) => {
    const aDate = new Date(a.createdDate).getTime();
    const bDate = new Date(b.createdDate).getTime();
    return bDate - aDate;
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Claims</h5>
        <Button
          variant="primary"
          className="rounded"
          onClick={openCreate}
          disabled={isDraftApplication}
        >
          Add Claim
        </Button>
      </div>

      {claims.length === 0 ? (
        <Alert variant="info" className="mb-0">
          <p className="mb-0">
            {isDraftApplication
              ? 'No claims have been recorded for this application.'
              : 'No claims have been recorded for this application. Click "Add Claim" to create one.'}
          </p>
        </Alert>
      ) : (
        <Table striped bordered hover responsive className="align-top">
          <thead>
            <tr>
              <th>ID</th>
              <th>{CLAIM_FIELD_LABELS.createdDate}</th>
              <th>{CLAIM_FIELD_LABELS.submitted}</th>
              <th>{CLAIM_FIELD_LABELS.claimSubmittedDate}</th>
              <th>{CLAIM_FIELD_LABELS.deliveryMethod}</th>
              <th>{CLAIM_FIELD_LABELS.receivedByUserId}</th>
              <th>{CLAIM_FIELD_LABELS.chargeOffDate}</th>
              <th className="text-end">{CLAIM_FIELD_LABELS.filedAmount}</th>
              <th className="text-end">{CLAIM_FIELD_LABELS.approvedClaimAmount}</th>
              <th>{CLAIM_FIELD_LABELS.amountPaidDate}</th>
              <th>{CLAIM_FIELD_LABELS.loanSold}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedClaims.map((c) => (
              <tr key={c.id}>
                <td>{c.claId != null ? c.claId : c.id}</td>
                <td>{formatDate(c.createdDate)}</td>
                <td>{c.submitted == null ? '—' : c.submitted ? 'Yes' : 'No'}</td>
                <td>{formatDate(c.claimSubmittedDate)}</td>
                <td>
                  {DELIVERY_METHOD_OPTIONS.find((o) => o.value === c.deliveryMethod)
                    ?.label ?? c.deliveryMethod ?? '—'}
                </td>
                <td>
                  {c.receivedBy
                    ? getEdoUserDisplayName(c.receivedBy)
                    : (c.receivedByUserId
                        ? (() => {
                            const u = edoUsers.find(
                              (x) => String(x.id) === String(c.receivedByUserId)
                            );
                            return u ? getEdoUserDisplayName(u) : c.receivedByUserId;
                          })()
                        : '—')}
                </td>
                <td>{formatDate(c.chargeOffDate)}</td>
                <td className="text-end">{formatClaimAmount(c.filedAmount, 'filedAmount')}</td>
                <td className="text-end">{formatClaimAmount(c.approvedClaimAmount, 'approvedClaimAmount')}</td>
                <td>{formatDate(c.amountPaidDate)}</td>
                <td>
                  {c.loanSold == null || c.loanSold === ''
                    ? '—'
                    : c.loanSold === 'Y' ||
                        c.loanSold === '1' ||
                        String(c.loanSold).toLowerCase() === 'true'
                      ? 'Yes'
                      : 'No'}
                </td>
                <td className="text-nowrap">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="rounded mr-1"
                    onClick={() => openEdit(c)}
                    disabled={deletingId != null}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="rounded"
                    onClick={() => handleDelete(c)}
                    disabled={deletingId != null}
                  >
                    {deletingId === c.id ? 'Deleting…' : 'Delete'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title className="mb-0">{editingId != null ? 'Edit Claim' : 'Add Claim'}</Modal.Title>
          <CloseModalButton onCloseModal={closeModal} />
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Row 1: Claim Submitted */}
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="claim-form-submitted"
                    label={CLAIM_FIELD_LABELS.submitted}
                    checked={form.submitted === true}
                    onChange={(e) => setField('submitted', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
            {/* Row 2: Claim Submitted Date (left) */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.claimSubmittedDate}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formatDateForInput(form.claimSubmittedDate) || ''}
                    onChange={(e) => setField('claimSubmittedDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.chargeOffDate}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formatDateForInput(form.chargeOffDate) || ''}
                    onChange={(e) => setField('chargeOffDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6} />
            </Row>
            {/* Row 4: Amount Filed (left) */}
            <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.deliveryMethod}</Form.Label>
                  <Form.Control
                    as="select"
                    value={String(form.deliveryMethod)}
                    onChange={(e) => setField('deliveryMethod', e.target.value)}
                  >
                    {DELIVERY_METHOD_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.filedAmount}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={amountDisplayValue('filedAmount')}
                      disabled={saving}
                      onFocus={() => handleAmountFocus('filedAmount')}
                      onBlur={() => handleAmountBlur('filedAmount')}
                      onChange={(e) =>
                        setAmountRaw((prev) => ({ ...prev, filedAmount: e.target.value }))
                      }
                      placeholder="0.00"
                      inputMode="decimal"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6} />
            </Row>
            {/* Row 5: Delivery Method (left) | Claim Amount Approved (right) */}
            <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.receivedByUserId}</Form.Label>
                  <Form.Control
                    as="select"
                    value={String(form.receivedByUserId ?? '')}
                    onChange={(e) => setField('receivedByUserId', e.target.value || '')}
                  >
                    <option value="">—</option>
                    {edoUsers.map((user) => (
                      <option key={user.id} value={String(user.id)}>
                        {getEdoUserDisplayName(user)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.approvedClaimAmount}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={amountDisplayValue('approvedClaimAmount')}
                      disabled={saving}
                      onFocus={() => handleAmountFocus('approvedClaimAmount')}
                      onBlur={() => handleAmountBlur('approvedClaimAmount')}
                      onChange={(e) =>
                        setAmountRaw((prev) => ({ ...prev, approvedClaimAmount: e.target.value }))
                      }
                      placeholder="0.00"
                      inputMode="decimal"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            {/* Row 6: CalCAP Loan # (application) | Lender CalCAP ID (lender org) | Date Claim Amount Paid */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>CalCAP Loan #</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    value={calcapLoanNumber ?? '—'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Lender CalCAP ID</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    value={lenderCalcapIdDisplay}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{CLAIM_FIELD_LABELS.amountPaidDate}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formatDateForInput(form.amountPaidDate) || ''}
                    onChange={(e) => setField('amountPaidDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            {/* Bottom: Lender Loan # (read-only) */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-0">
                  <Form.Label>Lender Loan #</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    value={lenderLoanNumber ?? '—'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="claim-form-loan-sold"
                      label={CLAIM_FIELD_LABELS.loanSold}
                      checked={form.loanSold === true}
                      onChange={(e) => setField('loanSold', e.target.checked)}
                    />
                  </Form.Group>
                </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : editingId != null ? 'Update Claim' : 'Create Claim'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApplicationClaimsTab;
